import { z } from 'zod';
import {
  envelopeSchema,
  storedEnvelopeSchema,
  type Envelope,
  type StoredEnvelope,
} from './envelope.js';
import { EVENT_SCHEMAS, isKnownEventType, type EventType, type PayloadOf } from './registry.js';

export * from './envelope.js';
export * from './registry.js';
export * from './etiquetas.js';
export * from './campos.js';

/** A validated event: envelope plus a payload narrowed by its type. */
export type DomainEvent = {
  [K in EventType]: Omit<Envelope, 'type' | 'payload'> & { type: K; payload: PayloadOf<K> };
}[EventType];

export type StoredDomainEvent = {
  [K in EventType]: Omit<StoredEnvelope, 'type' | 'payload'> & { type: K; payload: PayloadOf<K> };
}[EventType];

export type AnyDomainEvent = DomainEvent | StoredDomainEvent;

export class EventValidationError extends Error {
  constructor(
    message: string,
    readonly detail?: unknown,
    /**
     * A text longer than its field takes: which field, and how many characters it takes. For the
     * screen to say so in words, where it said «Payload inválido… String must contain at most 200
     * character(s)» (2026-10-13).
     */
    readonly largo?: { campo: string; maximo: number },
  ) {
    super(message);
    this.name = 'EventValidationError';
  }
}

/** The first text too long for its field, if that is what is wrong. */
function textoLargo(error: z.ZodError): { campo: string; maximo: number } | undefined {
  const issue = error.issues.find((i) => i.code === 'too_big' && i.type === 'string');
  if (!issue || issue.code !== 'too_big') return undefined;
  const campo = [...issue.path].reverse().find((p): p is string => typeof p === 'string') ?? '';
  return { campo, maximo: Number(issue.maximum) };
}

/**
 * Turns Zod's issue list into one line a person can act on.
 *
 * The full `flatten()` goes in `detail`, but the message is what gets stored on a rejected
 * event and shown in Ajustes — so it has to name the field. "Payload inválido" on its own
 * tells whoever has to fix it nothing at all.
 */
function resumirIssues(error: z.ZodError): string {
  const partes = error.issues.slice(0, 3).map((i) => {
    const campo = i.path.join('.');
    return campo ? `${campo}: ${i.message}` : i.message;
  });
  const resto = error.issues.length - partes.length;
  return partes.join('; ') + (resto > 0 ? ` (y ${resto} más)` : '');
}

function validatePayload(type: string, payload: unknown): unknown {
  if (!isKnownEventType(type)) {
    throw new EventValidationError(`Tipo de evento desconocido: ${type}`);
  }
  const result = (EVENT_SCHEMAS[type] as z.ZodTypeAny).safeParse(payload);
  if (!result.success) {
    throw new EventValidationError(
      `Payload inválido para ${type} — ${resumirIssues(result.error)}`,
      result.error.flatten(),
      textoLargo(result.error),
    );
  }
  const donde = textoQueNoSeGuarda(result.data);
  if (donde !== null) {
    throw new EventValidationError(`Payload inválido para ${type} — ${donde || 'texto'}: tiene un carácter que no se puede guardar`);
  }
  return result.data;
}

/**
 * Where in it there is text the server's database will not keep, or null: a NUL, or half of the
 * pair of code units an emoji is written with (a name cut in the middle of one). Such an event
 * passed every check here and failed only as the server stored it — and with it the whole push of
 * the phone that sent it, again and again. So it is refused where it is made, saying where.
 */
export function textoQueNoSeGuarda(x: unknown, ruta = ''): string | null {
  if (typeof x === 'string') return textoGuardable(x) ? null : ruta;
  if (Array.isArray(x)) {
    for (const [i, v] of x.entries()) {
      const r = textoQueNoSeGuarda(v, ruta ? `${ruta}.${i}` : String(i));
      if (r !== null) return r;
    }
    return null;
  }
  if (typeof x === 'object' && x !== null) {
    for (const [k, v] of Object.entries(x)) {
      const r = textoQueNoSeGuarda(v, ruta ? `${ruta}.${k}` : k);
      if (r !== null) return r;
    }
  }
  return null;
}

function textoGuardable(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c === 0) return false;
    if (c >= 0xd800 && c <= 0xdbff) {
      const siguiente = s.charCodeAt(i + 1);
      if (!(siguiente >= 0xdc00 && siguiente <= 0xdfff)) return false;
      i++;
    } else if (c >= 0xdc00 && c <= 0xdfff) {
      return false;
    }
  }
  return true;
}

/** Parse an unsynced event, as produced on a device. */
export function parseEvent(raw: unknown): DomainEvent {
  const env = envelopeSchema.safeParse(raw);
  if (!env.success) {
    throw new EventValidationError(`Envelope inválido — ${resumirIssues(env.error)}`, env.error.flatten());
  }
  const payload = validatePayload(env.data.type, env.data.payload);
  return { ...env.data, payload } as DomainEvent;
}

/** Parse an event the server has already sequenced. */
export function parseStoredEvent(raw: unknown): StoredDomainEvent {
  const env = storedEnvelopeSchema.safeParse(raw);
  if (!env.success) {
    throw new EventValidationError(`Envelope inválido — ${resumirIssues(env.error)}`, env.error.flatten());
  }
  const payload = validatePayload(env.data.type, env.data.payload);
  return { ...env.data, payload } as StoredDomainEvent;
}

/** Non-throwing variant, for a sync push where one bad event must not reject the batch. */
export function tryParseEvent(
  raw: unknown,
): { ok: true; event: DomainEvent } | { ok: false; error: string; detail?: unknown } {
  try {
    return { ok: true, event: parseEvent(raw) };
  } catch (e) {
    if (e instanceof EventValidationError) return { ok: false, error: e.message, detail: e.detail };
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** The same, for an event the server has already sequenced — used when replaying the log. */
export function tryParseStoredEvent(
  raw: unknown,
): { ok: true; event: StoredDomainEvent } | { ok: false; error: string; detail?: unknown } {
  try {
    return { ok: true, event: parseStoredEvent(raw) };
  } catch (e) {
    if (e instanceof EventValidationError) return { ok: false, error: e.message, detail: e.detail };
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
